import { useState, useEffect, useMemo } from "react"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { type Client } from "@/data/adminData"
import { adminTenantModulesApi, adminAvatarsApi, adminActivityApi, adminSystemApi, type TenantModuleDTO, type AvatarAssetDTO, type ActivityEventDTO } from "@/lib/api"
import { Zap, Users, Clock, ChevronDown, Puzzle } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

function ProgressBar({ used, total, h = 6 }: { used: number; total: number; h?: number }) {
  const pct = Math.min(100, (used / total) * 100)
  const color = pct > 85 ? 'oklch(0.58 0.17 28)' : pct > 65 ? 'oklch(0.65 0.15 58)' : 'oklch(0.145 0 0)'
  return (
    <div className="rounded-full overflow-hidden bg-secondary" style={{ height: h }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.3s' }} />
    </div>
  )
}

function StorageBar({ used, temp, total, h = 8 }: { used: number; temp: number; total: number; h?: number }) {
  const usedPct  = Math.min(100, (used  / total) * 100)
  const tempPct  = Math.min(100 - usedPct, (temp / total) * 100)
  return (
    <div className="rounded-full overflow-hidden bg-secondary flex" style={{ height: h }}>
      <div style={{ width: `${usedPct}%`, height: '100%', background: 'oklch(0.145 0 0)', transition: 'width 0.3s' }} />
      <div style={{ width: `${tempPct}%`, height: '100%', background: 'oklch(0.65 0.15 58)', transition: 'width 0.3s' }} />
    </div>
  )
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!on)} style={{ width:36, height:20, borderRadius:10, border:'none', background: on ? 'oklch(0.145 0 0)' : 'oklch(0.84 0 0)', position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0, padding:0 }}>
      <span style={{ position:'absolute', top:2, left: on ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.18s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )
}


export function ClientsView({ clients, onSelect }: { clients: Client[]; onSelect: (c: Client) => void }) {
  const [q, setQ] = useState('')
  const filtered = useMemo(() => clients.filter(c =>
    c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase())
  ), [clients, q])
  return (
    <div className="flex flex-col gap-5">
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b">
          <Input placeholder="Search clients…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs h-8 text-sm" />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Storage</TableHead>
              <TableHead>Features</TableHead>
              <TableHead>Studio API</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => {
              const pct = Math.round(((c.storage.used + c.storage.temp) / c.storage.total) * 100)
              const features = [
                c.avatarProduct && 'Avatar Product',
                c.studioApi && 'Studio API',
              ].filter(Boolean) as string[]
              return (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => onSelect(c)}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                        {c.name.slice(0,2).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="w-32">
                      <div className="text-xs text-muted-foreground mb-1">{c.storage.used} / {c.storage.total} GB ({pct}%)</div>
                      <StorageBar used={c.storage.used} temp={c.storage.temp} total={c.storage.total} h={4} />
                    </div>
                  </TableCell>
                  <TableCell>
                    {features.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {features.map(f => (
                          <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{f}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${c.studioApi ? 'bg-blue-500' : 'bg-border'}`} />
                      <span className="text-xs">{c.studioApi ? 'Enabled' : 'Off'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-secondary text-muted-foreground'}`}>
                      {c.status}
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No clients found</div>}
      </Card>
    </div>
  )
}





function AvatarModalContent({ avatars, selected, onToggle, onSetAll }: {
  avatars: AvatarAssetDTO[]; selected: number[]; onToggle: (id: number) => void; onSetAll: (ids: number[]) => void
}) {
  const [q, setQ] = useState('')
  const [preAuth, setPreAuth] = useState<number[] | null>(null)
  const allIds = avatars.map(a => a.id)
  const allSelected = allIds.length > 0 && allIds.every(id => selected.includes(id))

  const handleAuthAll = () => {
    if (allSelected && preAuth !== null) { onSetAll(preAuth); setPreAuth(null) }
    else { setPreAuth([...selected]); onSetAll(allIds) }
  }

  const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
  const filtered = avatars.filter(av => av.name.toLowerCase().includes(q.toLowerCase()))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Search avatar…" value={q} onChange={e => setQ(e.target.value)} className="h-8 text-sm max-w-xs" />
        <span className="text-xs text-muted-foreground ml-1">{selected.length} / {avatars.length} selected</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-medium">Auth CLI to use all</span>
          <Toggle on={allSelected} onChange={handleAuthAll} />
        </div>
      </div>
      {avatars.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">No avatar assets found. Add some in the Avatars page.</div>
      ) : (
        <div className="overflow-y-auto max-h-[75vh]">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
            {filtered.map(av => {
              const on = selected.includes(av.id)
              const imgSrc = av.img_path.startsWith('/') ? av.img_path : `${BASE}/${av.img_path}`
              return (
                <div key={av.id} onClick={() => onToggle(av.id)}
                  className={`relative flex flex-col rounded-xl border-2 cursor-pointer transition-all overflow-hidden ${on ? 'border-green-500 shadow-md shadow-green-100' : 'border-border opacity-50 hover:opacity-75'}`}>
                  {on && (
                    <div className="absolute top-2 left-2 z-10 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><path d="M20 6L9 17l-5-5"/></svg>
                    </div>
                  )}
                  <div className="w-full aspect-[2/3] overflow-hidden bg-secondary">
                    <img src={imgSrc} alt={av.name} className="w-full h-full object-cover object-top" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                  </div>
                  <div className="p-2.5 text-center">
                    <div className="text-xs font-semibold truncate">{av.name}</div>
                  </div>
                </div>
              )
            })}
            {filtered.length === 0 && <div className="col-span-5 py-8 text-center text-sm text-muted-foreground">No avatars found</div>}
          </div>
        </div>
      )}
    </div>
  )
}

export function NewClientForm({ onCancel, onCreate }: {
  onCancel: () => void
  onCreate: (body: { name: string; email: string; password: string; storage_quota_gb: number; credits_budget: number }) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [quota, setQuota] = useState('15')
  const [budget, setBudget] = useState('0')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !email || !password) return
    setSaving(true)
    setErr(null)
    try {
      await onCreate({ name, email, password, storage_quota_gb: Number(quota), credits_budget: Number(budget) })
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to create client')
      setSaving(false)
    }
  }

  return (
    <div className="max-w-md flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">New Client</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Create a new tenant account</p>
      </div>
      <Card className="p-5">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Company name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="SOS Lunettes" required />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@company.com" required />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Password</label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Storage quota (GB)</label>
              <Input type="number" value={quota} onChange={e => setQuota(e.target.value)} min="1" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Credits budget</label>
              <Input type="number" value={budget} onChange={e => setBudget(e.target.value)} min="0" />
            </div>
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={saving} className="flex-1">{saving ? 'Creating…' : 'Create client'}</Button>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ClientDetail({ client: init, clients, assets, onBack: _onBack, onUpdate, onDelete }: {
  client: Client; clients: Client[]; assets: AvatarAssetDTO[]; onBack: () => void
  onUpdate: (id: number, patch: Partial<Client>) => void
  onDelete: (id: number) => Promise<void>
}) {
  const [avatarModal, setAvatarModal] = useState(false)
  const [creditsModal, setCreditsModal] = useState(false)
  const [storageModal, setStorageModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [budgetVal, setBudgetVal] = useState('')
  const [tenantModules, setTenantModules] = useState<TenantModuleDTO[]>([])
  const [clientActivity, setClientActivity] = useState<ActivityEventDTO[]>([])
  const [usage, setUsage] = useState({ vision: 0, generation: 0 })

  useEffect(() => {
    adminSystemApi.getUsage(init.id).then(rows => setUsage({
      vision: rows.filter(r => r.kind === 'vision').reduce((acc, r) => acc + r.cost_eur, 0),
      generation: rows.filter(r => r.kind === 'generation').reduce((acc, r) => acc + r.cost_eur, 0),
    })).catch(() => {})
  }, [init.id])
  const client = clients.find(c => c.id === init.id) || init

  useEffect(() => {
    adminTenantModulesApi.list(client.id).then(setTenantModules).catch(() => {})
    adminActivityApi.list(50).then(all => setClientActivity(all.filter(a => a.tenant_name === client.name))).catch(() => {})
  }, [client.id, client.name])

  const toggleAvatar = async (aid: number) => {
    const has = client.avatars.includes(aid)
    try {
      if (has) await adminAvatarsApi.revokeFromTenant(client.id, aid)
      else await adminAvatarsApi.grantToTenant(client.id, aid)
      onUpdate(client.id, { avatars: has ? client.avatars.filter(a => a !== aid) : [...client.avatars, aid] })
    } catch (e) {
      console.error("Avatar toggle failed", e)
    }
  }

  return (
    <div className="flex flex-col gap-4">

      {/* Row 1: Info + Storage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Client Info */}
        <Card className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Client Info</div>
            {deleteConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Delete?</span>
                <Button size="sm" variant="destructive" disabled={deleting} onClick={async () => { setDeleting(true); await onDelete(client.id) }}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(true)}>Delete client</Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-base font-bold text-muted-foreground flex-shrink-0">
              {client.name.slice(0,2).toUpperCase()}
            </div>
            <div>
              <div className="text-base font-semibold">{client.name}</div>
              <div className="text-xs text-muted-foreground">{client.email}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`ml-auto flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full transition-colors ${client.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-200' : client.status === 'suspended' ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'}`}>
                  {client.status}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(['active', 'inactive', 'suspended'] as const).map(s => (
                  <DropdownMenuItem key={s} onClick={() => onUpdate(client.id, { status: s })}
                    className={client.status === s ? 'font-semibold' : ''}>
                    {s}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex gap-3 pt-1">
            <div className={`rounded-lg bg-secondary/60 px-3 py-2.5 ${client.studioApi ? 'flex-1' : 'flex-1'}`}>
              <div className="text-[10px] text-muted-foreground mb-0.5">Joined</div>
              <div className="text-sm font-semibold">{client.joined}</div>
            </div>
            {client.studioApi && (
              <div className="rounded-lg bg-secondary/60 px-3 py-2.5 flex-1 flex flex-col gap-1.5">
                <div className="text-[10px] text-muted-foreground">Credits used · {Math.round((client.credits.used/client.credits.budget)*100)}% <span className="text-muted-foreground">({(client.credits.budget - client.credits.used).toLocaleString()} remaining)</span></div>
                <div className="text-sm font-semibold">{client.credits.used.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">/ {client.credits.budget.toLocaleString()}</span></div>
                <ProgressBar used={client.credits.used} total={client.credits.budget} h={4} />
              </div>
            )}
            <div className="rounded-lg bg-secondary/60 px-3 py-2.5 flex-1">
              <div className="text-[10px] text-muted-foreground mb-0.5">AI cost</div>
              <div className="text-sm font-semibold">€{(usage.vision + usage.generation).toFixed(2)}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">vision €{usage.vision.toFixed(2)} · generation €{usage.generation.toFixed(2)}</div>
            </div>
          </div>
        </Card>

        {/* Storage */}
        <Card className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Storage</div>
            <button onClick={() => setStorageModal(true)}
              className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-border hover:bg-secondary transition-colors">
              Edit
            </button>
          </div>
          <StorageBar used={client.storage.used} temp={client.storage.temp} total={client.storage.total} h={8} />
          <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{background:'oklch(0.145 0 0)'}} />Used</span>
            <span className="flex items-center gap-1 ml-2"><span className="w-2 h-2 rounded-full inline-block" style={{background:'oklch(0.65 0.15 58)'}} />Temp</span>
            <span className="flex items-center gap-1 ml-2"><span className="w-2 h-2 rounded-full inline-block bg-secondary border border-border" />Free</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label:'Used', value: client.storage.used,  color:'oklch(0.145 0 0)' },
              { label:'Temp', value: client.storage.temp,  color:'oklch(0.65 0.15 58)' },
              { label:'Free', value: +(client.storage.total - client.storage.used - client.storage.temp).toFixed(1), color: undefined },
              { label:'Total', value: client.storage.total, color: undefined },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg bg-secondary/60 px-3 py-2.5">
                <div className="text-[10px] text-muted-foreground mb-0.5">{label}</div>
                <div className="text-sm font-semibold" style={color ? { color } : undefined}>
                  {value} <span className="text-xs font-normal text-muted-foreground">GB</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Row 2: Feature Access + Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Feature Access */}
        <div className="flex flex-col gap-4">
          <Card className="overflow-hidden p-0">
            <div className="px-5 pt-4 pb-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Feature Access</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">


                {/* Tile: Studio API */}
                <div className={`rounded-xl border-[1.5px] p-4 flex flex-col gap-3 transition-colors ${client.studioApi ? 'border-foreground bg-secondary/30' : 'border-border'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                      <Zap className="w-4 h-4" />
                    </div>
                    <Toggle on={client.studioApi} onChange={v => { onUpdate(client.id, { studioApi: v }); setCreditsModal(false) }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Studio API</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {client.studioApi ? 'Shared API credits' : 'Own API keys'}
                    </div>
                  </div>
                  {client.studioApi && (
                    <button onClick={() => { setCreditsModal(true); setBudgetVal(String(client.credits.budget)) }}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-border hover:bg-secondary transition-colors w-fit">
                      Manage credits
                    </button>
                  )}
                </div>
              </div>
            </div>

          </Card>

          {/* Avatar Dialog */}
          <Dialog open={avatarModal} onOpenChange={setAvatarModal}>
            <DialogContent className="max-w-[95vw]">
              <DialogHeader>
                <DialogTitle>Avatar Access — {client.name}</DialogTitle>
              </DialogHeader>
              <AvatarModalContent
                avatars={assets}
                selected={client.avatars}
                onToggle={toggleAvatar}
                onSetAll={async ids => {
                  const toGrant = ids.filter(id => !client.avatars.includes(id))
                  const toRevoke = client.avatars.filter(id => !ids.includes(id))
                  await Promise.all([
                    ...toGrant.map(id => adminAvatarsApi.grantToTenant(client.id, id).catch(() => {})),
                    ...toRevoke.map(id => adminAvatarsApi.revokeFromTenant(client.id, id).catch(() => {})),
                  ])
                  onUpdate(client.id, { avatars: ids })
                }}
              />
            </DialogContent>
          </Dialog>

          {/* Storage Dialog */}
          <Dialog open={storageModal} onOpenChange={setStorageModal}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Storage — {client.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 pt-1">
                <StorageBar used={client.storage.used} temp={client.storage.temp} total={client.storage.total} h={8} />
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label:'Used',  value: client.storage.used },
                    { label:'Temp',  value: client.storage.temp },
                    { label:'Free',  value: +(client.storage.total - client.storage.used - client.storage.temp).toFixed(1) },
                    { label:'Total', value: client.storage.total },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg bg-secondary/60 px-3 py-2">
                      <div className="text-[10px] text-muted-foreground">{label}</div>
                      <div className="text-sm font-semibold">{value} <span className="text-xs font-normal text-muted-foreground">GB</span></div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium">Storage limit</label>
                  <div className="flex gap-2 flex-wrap">
                    {[5,10,20,50,100].map(gb => {
                      const minRequired = client.storage.used + client.storage.temp
                      const disabled = gb < minRequired
                      return (
                        <button key={gb}
                          onClick={() => { if(!disabled) onUpdate(client.id, { storage:{...client.storage, total:gb} }) }}
                          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${client.storage.total === gb ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-secondary'} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}>
                          {gb} GB
                        </button>
                      )
                    })}
                  </div>
                </div>
                <Button variant="outline" onClick={() => setStorageModal(false)}>Close</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Credits Dialog */}
          <Dialog open={creditsModal} onOpenChange={setCreditsModal}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Manage Credits — {client.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 pt-1">
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-2">
                    <span>{client.credits.used.toLocaleString()} used</span>
                    <span>{Math.round((client.credits.used/client.credits.budget)*100)}% of budget</span>
                  </div>
                  <ProgressBar used={client.credits.used} total={client.credits.budget} h={8} />
                  <div className="text-xs text-muted-foreground mt-1.5">{(client.credits.budget - client.credits.used).toLocaleString()} remaining</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium">Budget</label>
                  <Input value={budgetVal} onChange={e => setBudgetVal(e.target.value)} className="h-9" placeholder="e.g. 10000" />
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={() => { const n = parseInt(budgetVal); if(n>0) onUpdate(client.id,{credits:{...client.credits,budget:n}}); setCreditsModal(false) }}>Save</Button>
                  <Button variant="outline" onClick={() => setCreditsModal(false)}>Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Activity */}
        <Card className="p-5 flex flex-col gap-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Activity</div>
          {clientActivity.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-10">No activity yet</div>
          ) : (
            <div className="flex flex-col divide-y">
              {clientActivity.map(a => (
                <div key={a.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{a.job_name}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                      <span className={`font-medium ${a.status === 'done' ? 'text-green-600' : a.status === 'error' ? 'text-destructive' : ''}`}>{a.status}</span>
                      <Clock className="w-3 h-3" />{new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Row 3: Modules */}
      {tenantModules.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="px-5 pt-4 pb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Modules</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {tenantModules.map(mod => {
              const isAvatarMod = mod.module_id === 'add_product_on_avatar'
              return (
                <div key={mod.module_id} className={`rounded-xl border-[1.5px] p-4 flex flex-col gap-3 transition-colors ${mod.enabled ? 'border-foreground bg-secondary/30' : 'border-border'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                      {isAvatarMod ? <Users className="w-4 h-4" /> : <Puzzle className="w-4 h-4" />}
                    </div>
                    <Toggle on={mod.enabled} onChange={async v => {
                      const updated = await adminTenantModulesApi.toggle(client.id, mod.module_id, v).catch(() => null)
                      if (updated) setTenantModules(prev => prev.map(m => m.module_id === mod.module_id ? { ...m, enabled: v } : m))
                    }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{mod.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {isAvatarMod && mod.enabled
                        ? `${client.avatars.length} / ${assets.length} avatars`
                        : mod.description}
                    </div>
                  </div>
                  {isAvatarMod && mod.enabled && (
                    <button onClick={() => setAvatarModal(true)}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-border hover:bg-secondary transition-colors w-fit">
                      Manage avatars
                    </button>
                  )}
                </div>
              )
            })}
          </div>
          </div>
        </Card>
      )}
    </div>
  )
}
